import { useRef, useState } from 'react';

import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface InteractiveObjectProps {
  geometry: 'box' | 'sphere' | 'cylinder';
  position: [number, number, number];
  name: string;
  color: string;
  onHover: (name: string | null) => void;
  onClick: (name: string) => void;
  isHovered: boolean;
}

export function InteractiveObject({
  geometry,
  position,
  name,
  color,
  onHover,
  onClick,
  isHovered,
}: InteractiveObjectProps) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);

  // Create outline effect when hovered
  useFrame(() => {
    if (meshRef.current) {
      if (hovered || isHovered) {
        meshRef.current.scale.set(1.05, 1.05, 1.05);
      } else {
        meshRef.current.scale.set(1, 1, 1);
      }
    }
  });

  const Geometry = () => {
    switch (geometry) {
      case 'box':
        return <boxGeometry args={[1, 1, 1]} />;
      case 'sphere':
        return <sphereGeometry args={[0.5, 32, 32]} />;
      case 'cylinder':
        return <cylinderGeometry args={[0.5, 0.5, 1, 32]} />;
    }
  };

  return (
    <mesh
      ref={meshRef}
      position={position}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        onHover(name);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
        onHover(null);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(name);
      }}
    >
      <Geometry />
      <meshStandardMaterial color={color} transparent opacity={hovered || isHovered ? 0.8 : 1} />
      {(hovered || isHovered) && (
        <lineSegments>
          <edgesGeometry
            args={[
              geometry === 'box'
                ? new THREE.BoxGeometry(1.2, 1.2, 1.2)
                : geometry === 'sphere'
                  ? new THREE.SphereGeometry(0.6, 32, 32)
                  : new THREE.CylinderGeometry(0.6, 0.6, 1.2, 32),
            ]}
          />
          <lineBasicMaterial color="#B3EC49" />
        </lineSegments>
      )}
    </mesh>
  );
}
